// Tiller Sync > Upload History — list of past uploads with parse_summary,
// who/when, click-through to per-upload resolutions detail.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { uploads } from '@/lib/api';
import type { UploadHistoryItem } from '@frt/shared-types';

export function TillerHistoryPage() {
  const [selected, setSelected] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const listQuery = useQuery({
    queryKey: ['uploads', { page }],
    queryFn: () => uploads.list({ page, per_page: 25 }),
  });
  const detailQuery = useQuery({
    queryKey: ['uploads', selected],
    queryFn: () => (selected ? uploads.get(selected) : Promise.resolve(null)),
    enabled: !!selected,
  });

  const items = listQuery.data?.data ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_24rem] gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Upload history ({total})</CardTitle>
        </CardHeader>
        <CardBody>
          {listQuery.error && (
            <div className="text-sm text-red-600">{(listQuery.error as Error).message}</div>
          )}
          {listQuery.isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-1 pr-3">When</th>
                    <th className="py-1 pr-3">By</th>
                    <th className="py-1 pr-3">Filename</th>
                    <th className="py-1 pr-3">Status</th>
                    <th className="py-1 pr-3">Bal added</th>
                    <th className="py-1 pr-3">Tx added</th>
                    <th className="py-1 pr-3">Resolved</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((u: UploadHistoryItem) => (
                    <tr
                      key={u.id}
                      onClick={() => setSelected(u.id)}
                      className={
                        'cursor-pointer border-t border-slate-100 hover:bg-slate-50 ' +
                        (selected === u.id ? 'bg-brand-50' : '')
                      }
                    >
                      <td className="py-1 pr-3 text-xs text-slate-600">
                        {u.uploaded_at.replace('T', ' ').slice(0, 19)}
                      </td>
                      <td className="py-1 pr-3 text-xs">
                        {u.uploaded_by_username ?? `#${u.uploaded_by_user_id}`}
                      </td>
                      <td className="py-1 pr-3 font-mono text-xs">{u.filename}</td>
                      <td className="py-1 pr-3">
                        <StatusBadge status={u.parse_status} />
                      </td>
                      <td className="py-1 pr-3 text-xs">
                        {u.parse_summary?.balances_added ?? 0}
                      </td>
                      <td className="py-1 pr-3 text-xs">
                        {u.parse_summary?.transactions_added ?? 0}
                      </td>
                      <td className="py-1 pr-3 text-xs">
                        {u.resolutions_applied_at ? (
                          <span className="text-emerald-600">✓</span>
                        ) : (
                          <span className="text-amber-600">pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 flex items-center justify-between text-sm">
            <div className="text-slate-500">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{selected ? `Upload #${selected}` : 'Pick an upload'}</CardTitle>
        </CardHeader>
        <CardBody className="text-sm space-y-3">
          {!selected && <p className="text-slate-500">Click a row to view its decisions.</p>}
          {selected && detailQuery.isLoading && <p className="text-slate-500">Loading…</p>}
          {selected && detailQuery.data && (
            <>
              <div className="space-y-1">
                <div className="text-xs text-slate-500">File</div>
                <div className="font-mono">{detailQuery.data.upload.filename}</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-slate-500">SHA-256</div>
                <div className="font-mono break-all text-xs">
                  {detailQuery.data.upload.file_hash}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-slate-500">Resolutions applied at</div>
                <div>
                  {detailQuery.data.upload.resolutions_applied_at ?? (
                    <span className="text-amber-600">— not yet —</span>
                  )}
                </div>
              </div>
              {detailQuery.data.parse_summary && (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Parse summary
                  </div>
                  <ul className="text-xs">
                    <li>
                      Rows processed: {detailQuery.data.parse_summary.rows_processed.balances}{' '}
                      balance / {detailQuery.data.parse_summary.rows_processed.transactions} tx
                    </li>
                    <li>
                      Inserted: {detailQuery.data.parse_summary.balances_added} balance /{' '}
                      {detailQuery.data.parse_summary.transactions_added} tx
                    </li>
                    <li>
                      Skipped (dup): {detailQuery.data.parse_summary.balances_skipped_dup} balance
                      / {detailQuery.data.parse_summary.transactions_skipped_dup} tx
                    </li>
                    <li>
                      Missing app accounts:{' '}
                      {detailQuery.data.parse_summary.missing_app_accounts.length}
                    </li>
                    <li>
                      Unmatched Tiller accounts:{' '}
                      {detailQuery.data.parse_summary.unmatched_tiller_accounts.length}
                    </li>
                    <li>Errors: {detailQuery.data.parse_summary.errors.length}</li>
                  </ul>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color =
    status === 'success'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'partial'
        ? 'bg-amber-100 text-amber-800'
        : status === 'failed'
          ? 'bg-red-100 text-red-700'
          : 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${color}`}>
      {status}
    </span>
  );
}
